/**
 * Next.js App Router error boundaries that record what they catch
 * (tdd-0110 component 4, Layer 2).
 *
 * ```tsx
 * // apps/<app>/app/global-error.tsx — the entire file
 * "use client";
 * export { GlobalError as default } from "@workspace/observability/browser/next";
 *
 * // apps/<app>/app/error.tsx — the entire file (route segment; keeps the layout)
 * "use client";
 * export { RouteError as default } from "@workspace/observability/browser/next";
 * ```
 *
 * ## Why these must record, rather than leave it to the window handlers
 *
 * Verified on Next 16.3.5 / React 19.2.3 (see `browser-errors.ts`): once an
 * app has `error.tsx` / `global-error.tsx`, the errors they catch never reach
 * `window` `error`. Adding the boundaries without recording in them would turn
 * every render error from "seen by Layer 1" into "seen by nobody".
 *
 * ## What is recorded
 *
 * A `browser.error` span with `error.source = react`, plus `next.digest`
 * (present for server-component errors, where the client only gets a generic
 * message — the digest is the join key to the server log line) and, for
 * `<ErrorBoundary>`, `react.component_stack`. Next does not hand
 * `componentStack` to `error.tsx`, so the route/global boundaries record what
 * they have.
 *
 * ## Styling
 *
 * Inline styles only. This package is not in `packages/ui`'s Tailwind
 * `@source` list, so utility classes here would be silently inert. The route
 * fallback inherits the app's colours and reads its `--primary` /
 * `--primary-foreground` / `--radius` tokens with fallbacks; the global one
 * replaces the root layout (so no app CSS is loaded) and leans on
 * `color-scheme: light dark` for system light/dark.
 *
 * Works under `output: "export"`: nothing here touches the server.
 */
"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Component, useEffect } from "react";
import { recordBrowserError } from "./browser-errors.js";
const panelStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.75rem",
    padding: "3rem 1rem",
    textAlign: "center",
    color: "inherit",
    fontFamily: "inherit",
};
const headingStyle = { margin: 0, fontSize: "1.125rem", fontWeight: 600 };
const bodyStyle = { margin: 0, maxWidth: "32rem", fontSize: "0.875rem", opacity: 0.8 };
const refStyle = {
    margin: 0,
    fontSize: "0.75rem",
    opacity: 0.7,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
};
const themedButtonStyle = {
    padding: "0.5rem 1rem",
    border: "none",
    borderRadius: "var(--radius, 0.375rem)",
    background: "var(--primary, #2563eb)",
    color: "var(--primary-foreground, #ffffff)",
    font: "inherit",
    fontSize: "0.875rem",
    fontWeight: 500,
    cursor: "pointer",
};
function ErrorPanel({ title, description, digest, onRetry, buttonStyle, minHeight, }) {
    return (_jsxs("div", { role: "alert", "data-slot": "error-panel", style: { ...panelStyle, minHeight }, children: [_jsx("h2", { style: headingStyle, children: title }), _jsx("p", { style: bodyStyle, children: description }), digest ? _jsxs("p", { style: refStyle, children: ["Reference: ", digest] }) : null, _jsx("button", { type: "button", onClick: onRetry, style: buttonStyle, children: "Try again" })] }));
}
/** Record once per distinct error object (Next re-renders the boundary). */
function useRecordError(error, boundary) {
    useEffect(() => {
        recordBrowserError(error, { source: "react", digest: error.digest, boundary });
    }, [error, boundary]);
}
/**
 * `app/error.tsx`: catches errors in a route segment, below the root layout,
 * so the app shell stays up. `reset` re-renders the segment.
 */
export function RouteError({ error, reset }) {
    useRecordError(error, "route");
    return (_jsx(ErrorPanel, { title: "Something went wrong", description: "This part of the page hit an error. Trying again often fixes it; if it keeps happening, reload the page.", digest: error.digest, onRetry: reset, buttonStyle: themedButtonStyle, minHeight: "40vh" }));
}
/**
 * `app/global-error.tsx`: catches errors in the root layout itself. It
 * replaces the root layout, so it must render `<html>` and `<body>`.
 */
export function GlobalError({ error, reset }) {
    useRecordError(error, "global");
    return (_jsxs("html", { lang: "en", style: { colorScheme: "light dark" }, children: [_jsx("head", { children: _jsx("title", { children: "Something went wrong" }) }), _jsx("body", { style: {
                    margin: 0,
                    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
                }, children: _jsx("main", { children: _jsx(ErrorPanel, { title: "Something went wrong", description: "The page failed to load. Try again, or reload if it keeps happening.", digest: error.digest, onRetry: reset, buttonStyle: { padding: "0.5rem 1rem", font: "inherit", cursor: "pointer" }, minHeight: "100vh" }) }) })] }));
}
/**
 * Opt-in island boundary: lets a heavy panel fail without taking the page
 * down. Telemetry does not need it (the route/global boundaries already see
 * everything); it is a UX decision. Unlike Next's boundaries it has the React
 * `componentStack`, which it records.
 */
export class ErrorBoundary extends Component {
    state = { error: null };
    static getDerivedStateFromError(error) {
        return { error: error instanceof Error ? error : new Error(String(error)) };
    }
    componentDidCatch(error, info) {
        recordBrowserError(error, {
            source: "react",
            componentStack: info.componentStack ?? undefined,
            boundary: this.props.name,
        });
    }
    reset = () => {
        this.setState({ error: null });
    };
    render() {
        const { error } = this.state;
        if (!error)
            return this.props.children;
        const { fallback } = this.props;
        if (typeof fallback === "function")
            return fallback({ error, reset: this.reset });
        if (fallback !== undefined)
            return fallback;
        return (_jsx(ErrorPanel, { title: "This section failed to load", description: "The rest of the page still works. Try again to reload this part.", onRetry: this.reset, buttonStyle: themedButtonStyle }));
    }
}
